import { Box, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Text, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import SwipeableViews from "react-swipeable-views";

interface WorkplacePhotosFullScreenDialogProps {
  photos: string[];
  modalState: UseModalState;
  currentPhotoIndex: number;
  onPhotoIndexChange: (index: number, trigger: "swipe" | "tap") => void;
}

export function WorkplacePhotosFullScreenDialog(props: WorkplacePhotosFullScreenDialogProps) {
  const { photos, modalState, currentPhotoIndex, onPhotoIndexChange } = props;

  if (photos.length === 0) {
    return null;
  }

  return (
    <FullScreenDialog
      modalState={modalState}
      sx={{ "& .MuiPaper-root": { backgroundColor: (theme) => theme.palette.common.black } }}
    >
      <Stack sx={{ height: "100%", width: "100%" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ paddingX: 4, paddingY: 3 }}
        >
          <IconButton
            aria-label="Close photo viewer"
            iconType="close"
            variant="ghost"
            sx={{ color: (theme) => theme.palette.common.white }}
            onClick={() => modalState.closeModal()}
          />
          <Text variant="body2" sx={{ color: (theme) => theme.palette.common.white }}>
            {currentPhotoIndex + 1} / {photos.length}
          </Text>
        </Stack>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {currentPhotoIndex > 0 && (
            <IconButton
              aria-label="Previous photo"
              iconType="chevron-left"
              variant="ghost"
              sx={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1,
                color: "common.white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                borderRadius: "50%",
                "&:hover, &:focus, &:active": {
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                },
              }}
              onClick={() => onPhotoIndexChange(currentPhotoIndex - 1, "tap")}
            />
          )}

          <SwipeableViews
            enableMouseEvents
            index={currentPhotoIndex}
            style={{ width: "100%", height: "100%" }}
            containerStyle={{
              height: "100%",
              alignItems: "center",
            }}
            onChangeIndex={(index) => onPhotoIndexChange(index, "swipe")}
          >
            {photos.map((photo, index) => (
              <Box
                key={photo}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                <Box
                  component="img"
                  src={photo}
                  alt={`Workplace photo ${index + 1}`}
                  sx={{
                    objectFit: "contain",
                    maxWidth: "100%",
                    maxHeight: "100%",
                  }}
                />
              </Box>
            ))}
          </SwipeableViews>

          {currentPhotoIndex < photos.length - 1 && (
            <IconButton
              aria-label="Next photo"
              iconType="chevron-right"
              variant="ghost"
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1,
                color: "common.white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                borderRadius: "50%",
                "&:hover, &:focus, &:active": {
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                },
              }}
              onClick={() => onPhotoIndexChange(currentPhotoIndex + 1, "tap")}
            />
          )}
        </Box>
      </Stack>
    </FullScreenDialog>
  );
}
